import * as React from 'react';

import { Direction } from '~shared/utils/sort';
import {
  safeGetSessionStorageItem,
  safeRemoveSessionStorageItem,
  safeSetSessionStorageItem,
} from '~shared/utils/storage/sessionStorage';

export enum QuizListColumn {
  Title = 'Title',
  Owner = 'Owner',
  Modified = 'Modified',
}

type Sort = {
  quizListColumn: QuizListColumn;
  direction: Direction;
};

type NoSort = {
  quizListColumn: null;
  direction: null;
};

export type ColumnSortState = Sort | NoSort;
export type DispatchColumnSortAction = React.Dispatch<ColumnSortAction>;

type ResetAction = {
  type: 'Reset';
};

type ToggleColumnAction = {
  type: 'ToggleColumn';
  quizListColumn: QuizListColumn;
};

export type ColumnSortAction = ResetAction | ToggleColumnAction;

function columnSortReducer(
  state: ColumnSortState,
  action: ColumnSortAction,
  initialToggleDirection: Direction
) {
  switch (action.type) {
    case 'Reset':
      return {
        quizListColumn: null,
        direction: null,
      };
    case 'ToggleColumn':
      return {
        quizListColumn: action.quizListColumn,
        direction:
          state.quizListColumn !== action.quizListColumn
            ? initialToggleDirection
            : state.direction === Direction.Descending
            ? Direction.Ascending
            : Direction.Descending,
      };
  }
  return state;
}

const initializer = ({ quizListColumn: column, direction }: ColumnSortState) =>
  column && direction
    ? {
        quizListColumn: column,
        direction: direction,
      }
    : {
        quizListColumn: null,
        direction: null,
      };

interface UseColumnSortReducerParams {
  initialSortColumn?: QuizListColumn;
  initialSortDirection?: Direction;
  initialToggleDirection?: Direction;
  sessionStorageKey?: string;
}

function getPersistedColumnSort(
  sessionStorageKey?: string
): ColumnSortState | null {
  if (!sessionStorageKey) {
    return null;
  }

  const rawSort = safeGetSessionStorageItem(sessionStorageKey);

  if (!rawSort) {
    return null;
  }

  try {
    const parsedSort = JSON.parse(rawSort);

    if (
      Object.values(QuizListColumn).includes(parsedSort.column) &&
      Object.values(Direction).includes(parsedSort.direction)
    ) {
      return {
        quizListColumn: parsedSort.column,
        direction: parsedSort.direction,
      };
    }
  } catch {
    return null;
  }

  return null;
}

export const useColumnSortReducer = (
  {
    initialSortColumn = QuizListColumn.Modified,
    initialSortDirection = Direction.Descending,
    initialToggleDirection = Direction.Descending,
    sessionStorageKey,
  }: UseColumnSortReducerParams = {}
) => {
  const [state, dispatch] = React.useReducer(
    (
      currentState: ColumnSortState,
      action: ColumnSortAction
    ) => columnSortReducer(currentState, action, initialToggleDirection),
    {
      quizListColumn: initialSortColumn,
      direction: initialSortDirection,
    },
    (defaultState) =>
      getPersistedColumnSort(sessionStorageKey) ?? initializer(defaultState)
  );

  React.useEffect(() => {
    if (!sessionStorageKey) {
      return;
    }

    if (state.quizListColumn && state.direction) {
      safeSetSessionStorageItem(
        sessionStorageKey,
        JSON.stringify({ column: state.quizListColumn, direction: state.direction })
      );
      return;
    }

    safeRemoveSessionStorageItem(sessionStorageKey);
  }, [sessionStorageKey, state]);

  return [state, dispatch] as const;
};
